import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchEdiExtractStatus } from "../../../utils/apiUtils";

const CLOCK_TICK_MS = 30 * 1000;

function useNow(intervalMs) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
}

const FAILED_STATES = ["failed", "timed_out", "aborted"];

export function useEdiExtractStatus() {
    // re-render periodically so relative-time text stays current between
    // manual refreshes - this does not fetch anything
    useNow(CLOCK_TICK_MS);

    const { data, isLoading, isFetching, isError, refetch } = useQuery({
        queryKey: ["edi-extract-status"],
        queryFn: fetchEdiExtractStatus,
        retry: false,
        staleTime: Infinity,
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const state = data?.state;
    const failed = FAILED_STATES.includes(state);
    const disabled = isLoading || isError || state === "running";
    const failedJobs = failed
        ? (data?.jobs || []).filter((job) => job.status === "failed").map((job) => job.extractName)
        : [];
    const canRetry = failed && failedJobs.length > 0;

    return {
        data,
        isLoading,
        isFetching,
        isError,
        refetch,
        state,
        failed,
        disabled,
        failedJobs,
        canRetry,
    };
}

export function relativeTime(iso) {
    if (!iso) return null;
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins === 1) return "1 minute ago";
    if (mins < 60) return `${mins} minutes ago`;
    const hours = Math.round(mins / 60);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
}
